import { HeartHandshake, Repeat, Info } from 'lucide-react';
import { usePost } from '@/lib/useData';
import { SectionCard } from '@/components/common/SectionCard';
import { CardSkeleton } from '@/components/common/Loading';
import { fmtInt, fmtEur, fmtPct, fmtNum } from '@/lib/format';

// Dons réguliers = slate neutre (cohérent avec « BS signés »), Parrainage = accent tenant.
const REG = { color: '#5B6472', soft: 'rgba(91,100,114,0.12)' };
const PAR = { color: 'var(--tenant-accent-500)', soft: 'var(--tenant-accent-100)' };

const SegmentTile = ({ icon: Icon, name, range, palette, seg, testid }) => (
  <div className="relative overflow-hidden rounded-2xl border bg-card p-4 card-shadow" data-testid={testid}>
    <span className="absolute left-0 top-0 h-full w-1" style={{ background: palette.color }} />
    <div className="flex items-start justify-between gap-2">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: palette.soft }}>
          <Icon className="h-[18px] w-[18px]" style={{ color: palette.color }} />
        </span>
        <div>
          <p className="text-sm font-semibold text-foreground">{name}</p>
          <p className="text-[11px] text-muted-foreground">{range}</p>
        </div>
      </div>
      <span className="rounded-full px-2 py-0.5 text-xs font-semibold tabular"
        style={{ color: palette.color, background: palette.soft }} data-testid={`${testid}-pct-bs`}>
        {fmtPct(seg.pct_bs)}
      </span>
    </div>

    <div className="mt-3 flex items-end justify-between gap-2">
      <div>
        <p className="kpi-value text-2xl text-foreground" data-testid={`${testid}-bs`}>{fmtInt(seg.bs_reel)}</p>
        <p className="text-[11px] text-muted-foreground">BS validés</p>
      </div>
      <div className="text-right">
        <p className="font-display text-lg font-semibold tabular text-foreground" data-testid={`${testid}-montant`}>{fmtEur(seg.montant, 0)}</p>
        <p className="text-[11px] text-muted-foreground">collectés · {fmtPct(seg.pct_montant)}</p>
      </div>
    </div>

    <div className="mt-3 grid grid-cols-2 gap-2">
      <div className="flex items-center justify-between rounded-lg bg-muted/50 px-2.5 py-1.5">
        <span className="text-[11px] text-muted-foreground">Don moyen</span>
        <span className="text-sm font-semibold tabular text-foreground" data-testid={`${testid}-don-moyen`}>{fmtEur(seg.don_moyen)}</span>
      </div>
      <div className="flex items-center justify-between rounded-lg bg-muted/50 px-2.5 py-1.5">
        <span className="text-[11px] text-muted-foreground">Âge moyen</span>
        <span className="text-sm font-semibold tabular text-foreground" data-testid={`${testid}-age-moyen`}>{seg.age_moyen ? `${fmtNum(seg.age_moyen, 1)} ans` : '—'}</span>
      </div>
    </div>
  </div>
);

const ShareBar = ({ label, pctReg, pctPar }) => (
  <div>
    <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
    <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted" role="img" aria-label={label}>
      <div className="h-full transition-[width] duration-300" style={{ width: `${Math.round(pctReg * 100)}%`, background: REG.color }} />
      <div className="h-full transition-[width] duration-300" style={{ width: `${Math.round(pctPar * 100)}%`, background: PAR.color }} />
    </div>
  </div>
);

export function DonSegmentation({ body }) {
  const { data, isLoading } = usePost('/analytics/segmentation', body);

  if (isLoading) return <CardSkeleton />;
  if (!data || data.enabled === false) return null;

  const { segments, total, seuil, indetermine } = data;
  const reg = segments.regulier;
  const par = segments.parrainage;
  if (!total?.bs_reel) return null; // aucun don validé sur la période → on n'affiche rien

  return (
    <SectionCard
      title="Segmentation des dons"
      subtitle={`Dons réguliers vs parrainage · seuil ${fmtEur(seuil, 0)} / mois`}
      testid="don-segmentation-card"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SegmentTile
          icon={Repeat}
          name="Dons réguliers"
          range={`Moins de ${fmtEur(seuil, 0)} / mois`}
          palette={REG}
          seg={reg}
          testid="segment-regulier"
        />
        <SegmentTile
          icon={HeartHandshake}
          name="Parrainage"
          range={`${fmtEur(seuil, 0)} et plus / mois`}
          palette={PAR}
          seg={par}
          testid="segment-parrainage"
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ShareBar label="Part des BS validés" pctReg={reg.pct_bs} pctPar={par.pct_bs} />
        <ShareBar label="Part du montant collecté" pctReg={reg.pct_montant} pctPar={par.pct_montant} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: REG.color }} />
          <span className="text-muted-foreground">Dons réguliers</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: PAR.color }} />
          <span className="text-muted-foreground">Parrainage</span>
        </span>
        <span className="ml-auto text-muted-foreground">
          Total&nbsp;: <span className="font-semibold tabular text-foreground">{fmtInt(total.bs_reel)} BS</span>
          {' · '}<span className="font-semibold tabular text-foreground">{fmtEur(total.montant, 0)}</span>
        </span>
      </div>

      {indetermine?.bs_reel > 0 && (
        <p className="mt-3 flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {fmtInt(indetermine.bs_reel)} BS issus de données agrégées ne sont pas ventilables par donateur et sont exclus de la répartition ci-dessus.
        </p>
      )}
    </SectionCard>
  );
}
